<script lang="ts">
import type { SelectFieldOptions } from '#layers/block-engine/public';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { TileRadioGroupProps } from '#layers/ui/public';
import { formatTitle } from '@directus/format-title';
import { defineBlock, selectOptionsFormSchema, useSelectOptions } from '#layers/block-engine/public';

interface InputTileRadioBlockProps extends
	SelectFieldOptions,
	InputBlockProps,
	Pick<TileRadioGroupProps, 'size' | 'maxItems'> {
	/**
	 * Convert the value to title case when no explicit label is provided
	 *
	 * @default true
	 */
	autoTitleCaseValue?: boolean;
}

const __DsBlock = {
	type: 'input',
	name: '$t:blocks.input_tile_radio.name',
	description: '$t:blocks.input_tile_radio.description',
	icon: 'lucide:list-todo',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: { supportedTypes: ({ typeOptions }) => typeOptions?.multiple ? [] : ['select'] },
	options: ({ form, values }) => form([
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		selectOptionsFormSchema({ options: values.options }),
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'maxItems',
			type: 'number',
		},
		{
			name: 'autoTitleCaseValue',
			label: '$t:blocks.common.props.auto_title_case_value',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
	]),
}</script>

<script setup lang="ts">
import { computed } from 'vue';
import { BLOCK_CONTROL_SIZE_OPTIONS } from '#layers/blocks/constants/ui-options';
// TODO: Resolve circular dependency with `#layers/data-model` during built-in block metadata initialization.
import { usePrimitiveFormField } from '#layers/forms/public';
import { useTranslationUtils } from '#layers/i18n/public';
import { useDisabledNotice, useReadonly } from '#layers/ui/public';

const {
	autoTitleCaseValue = true,
	options,
	readonly,
	disabled: disabledProp,
	disabledNotice: disabledNoticeProp,
	size,
	maxItems,
} = defineProps<InputTileRadioBlockProps>();
const model = defineModel<any>();

;

const forwarded = computed(() => ({ size, maxItems }));
const { translateString } = useTranslationUtils();
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(() => ({ disabled: disabledProp, disabledNotice: disabledNoticeProp }), { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const { normalizedOptions } = useSelectOptions(() => options);
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => readonly);

const translatedItems = computed(() => normalizedOptions.value.map((item) => {
	const label = translateString(item.label);
	const description = translateString(item.description);
	return {
		...item,
		label: autoTitleCaseValue && label ? formatTitle(label) : label,
		description,
	};
}));
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DTileRadioGroup
			v-bind="forwarded"
			v-model="modelValue"
			:disabled
			:readonly
			:aria-readonly="readonly"
			:items="translatedItems"
			class="grid-cols-1 @w-xs:grid-cols-2"
			@pointerdown.capture="preventPointer"
			@click.capture="preventPointer"
			@keydown.capture="preventKeyboardNavigation"
		>
			<template v-if="$slots['item-icon']" #item-icon="slotProps">
				<slot name="item-icon" v-bind="slotProps" />
			</template>
		</DTileRadioGroup>
	</DTooltip>
</template>
